import { api } from '../lib/api';
import type { Page } from '../lib/api';
import type { HrAlertView } from '../lib/types';
import { useResource } from '../lib/useResource';
import { Card, Empty, Loading, Notice, StatusPill, exactly, when } from '../components/ui';

/**
 * The HR view.
 *
 * <p>Deliberately narrow. An HR reviewer sees findings that Technology has explicitly released,
 * with the context needed to start a conversation, and nothing else: no raw event stream, no
 * device browsing, no activity timeline. That boundary is enforced by the API, not by this page.
 */
export function HrQueue() {
  const alerts = useResource(
    () => api.get<Page<HrAlertView>>('/api/v1/alerts/hr-queue', { size: 50 }),
    [],
  );

  return (
    <>
      <div className="page-head">
        <h1>HR review queue</h1>
        <p>Findings Technology has formally released for HR review.</p>
      </div>

      <div className="caveat">
        These findings are an input to a process, not a conclusion. Monitoring data cannot on its own
        support an employment decision: any action follows the existing disciplinary procedure, and
        HR and Legal review the evidence before anything is put to the employee. Where a finding
        relates to application usage, remember it measures foreground time on a company device and
        misses work done in meetings, on the phone, or away from the machine.
      </div>

      <Card>
        {alerts.error && <Notice kind="error">{alerts.error}</Notice>}
        {alerts.loading && !alerts.data && <Loading />}
        {alerts.data?.content.length === 0 && (
          <Empty>Nothing has been released to HR for review.</Empty>
        )}

        {alerts.data && alerts.data.content.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Finding</th><th>Employee</th><th>Device</th><th>Severity</th>
                  <th className="num">Occurrences</th><th>Period</th><th>Released</th>
                </tr>
              </thead>
              <tbody>
                {alerts.data.content.map((alert) => (
                  <tr key={alert.id}>
                    <td>
                      <strong>{alert.title}</strong>
                      <div style={{ color: 'var(--muted)', fontSize: 12.5, maxWidth: '46ch' }}>
                        {alert.summary}
                      </div>
                    </td>
                    <td>{alert.assignedStaffName ?? 'unassigned device'}</td>
                    <td>{alert.assetTag ?? '—'}</td>
                    <td><StatusPill value={alert.severity} /></td>
                    <td className="num">{alert.occurrenceCount}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {new Date(alert.firstSeenAt).toLocaleDateString()} &ndash;{' '}
                      {new Date(alert.lastSeenAt).toLocaleDateString()}
                    </td>
                    <td style={{ fontSize: 12.5 }}>
                      {when(alert.hrReleasedAt)}
                      <div style={{ color: 'var(--faint)' }}>
                        by {alert.releasedByName ?? 'unknown'}
                      </div>
                      <div style={{ color: 'var(--faint)' }} title={exactly(alert.hrReleasedAt)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
